<template>
  <b-overlay :show="loading">
    <h5 class="mb-4">
      Verificación de identidad para firma electrónica avanzada
    </h5>
    <b-card>
      <!-- Selector de método de verificación -->
      <template v-if="step === 'SELECT_METHOD'">
        <div class="text-center mb-4">
          <p style="color: #4e4e4e;">
            Seleccione el método de verificación de identidad para generar su certificado de firma electrónica avanzada.
          </p>
        </div>
        <div class="row px-3">
          <div class="col-12 mb-4" v-if="nfcEnabled">
            <b-button
              variant="primary"
              block
              size="lg"
              @click="startVerification('NFC')"
            >
              <feather-icon icon="SmartphoneIcon" class="mr-2" />
              Verificación por NFC
            </b-button>
            <div class="bg-light rounded p-3 mt-2" style="border-left: 3px solid #7367f0;">
              <p class="mb-1" style="color: #4e4e4e; font-size: 0.85rem;">
                Se deberá descargar una aplicación para realizar una verificación de identidad con su dispositivo móvil.
              </p>
              <p class="mb-0" style="color: #4e4e4e; font-size: 0.85rem;">
                <strong>Su dispositivo debe contar con sensor NFC para continuar con esta opción.</strong>
              </p>
            </div>
          </div>
          <div class="col-12 mb-3" v-if="bankTransferEnabled">
            <b-button
              variant="outline-primary"
              block
              size="lg"
              @click="startVerification('BANK_TRANSFER')"
            >
              <feather-icon icon="DollarSignIcon" class="mr-2" />
              Verificación por Transferencia bancaria
            </b-button>
            <div class="bg-light rounded p-3 mt-2" style="border-left: 3px solid #7367f0;">
              <p class="mb-1" style="color: #4e4e4e; font-size: 0.85rem;">
                Se debe realizar una transferencia bancaria desde una cuenta a su nombre.
              </p>
              <p class="mb-0" style="color: #4e4e4e; font-size: 0.85rem;">
                El monto será reversado una vez confirmada su identidad.
              </p>
            </div>
          </div>
        </div>
      </template>

      <!-- NFC en proceso -->
      <template v-if="step === 'NFC_PENDING'">
        <div class="text-center">
          <div class="mb-3">
            <feather-icon icon="SmartphoneIcon" size="42" class="text-primary" />
          </div>
          <h5>Verificación NFC en proceso</h5>
          <div class="my-4">
            <div class="d-inline-block rounded-circle border border-primary p-3" style="width: 100px; height: 100px; line-height: 70px;">
              <span class="font-weight-bold" style="font-size: 1.5rem; color: #7367f0;">
                {{ countdownFormatted }}
              </span>
            </div>
            <p class="text-muted mt-2 small">Tiempo restante para completar la verificación</p>
          </div>
          <div class="bg-light rounded p-3 mb-4 text-left" style="border-left: 3px solid #7367f0;">
            <p class="mb-2" style="color: #4e4e4e; font-size: 0.9rem;">
              <strong>Descargue la aplicación Firmaki ID</strong> en su celular para completar la verificación NFC con su cédula de identidad.
            </p>
          </div>
          <div class="row mt-3">
            <div class="col-6 text-center">
              <a
                href="https://apps.apple.com/cl/app/firmaki-id/id6756390157"
                target="_blank"
                class="btn btn-outline-dark btn-block"
              >
                <feather-icon icon="SmartphoneIcon" size="14" class="mr-1" />
                App Store (iPhone)
              </a>
            </div>
            <div class="col-6 text-center">
              <a
                href="https://play.google.com/store/apps/details?id=id.amparo.firmaki"
                target="_blank"
                class="btn btn-outline-success btn-block"
              >
                <feather-icon icon="SmartphoneIcon" size="14" class="mr-1" />
                Google Play (Android)
              </a>
            </div>
          </div>
          <p v-if="countdownExpired" class="text-danger mt-4 font-weight-bold">
            El tiempo ha expirado. Por favor, inicie el proceso nuevamente.
          </p>
        </div>
      </template>

      <!-- Cargando preguntas -->
      <template v-if="step === 'LOADING_QUESTIONS'">
        <div class="text-center">
          <div class="mb-4">
            <feather-icon icon="CheckCircleIcon" size="48" class="text-success" />
          </div>
          <h5>Identidad verificada por NFC</h5>
          <p style="color: #4e4e4e; font-size: 0.95rem;">
            Estamos solicitando las preguntas de verificación. Espere un momento.
          </p>
          <div class="mt-3">
            <b-spinner variant="primary" label="Cargando preguntas..." />
          </div>
        </div>
      </template>

      <!-- Preguntas de seguridad -->
      <template v-if="step === 'QUESTIONS'">
        <div>
          <h5 class="mb-3">Preguntas de verificación de identidad</h5>
          <p style="color: #4e4e4e; font-size: 0.9rem;" class="mb-4">
            Responda las siguientes preguntas para completar el proceso.
          </p>
          <div v-for="(pregunta, index) in securityQuestions" :key="index" class="mb-4">
            <label class="font-weight-bold" style="color: #4e4e4e;">
              {{ pregunta.questionText }}
            </label>
            <b-form-radio-group
              v-if="pregunta.answerChoice && pregunta.answerChoice.length"
              v-model="securityAnswers[pregunta.questionId]"
              :options="formatOptions(pregunta.answerChoice)"
              stacked
              class="mt-2"
            />
          </div>

          <!-- Password del certificado -->
          <div class="row mt-4 mb-3">
            <div class="col-6">
              <vs-input
                autocomplete="off"
                class="w-100"
                label="Contraseña para el certificado"
                placeholder="Ingresar contraseña"
                maxlength="20"
                type="password"
                v-model="password"
              />
            </div>
            <div class="col-6">
              <vs-input
                autocomplete="off"
                class="w-100"
                label="Confirmar contraseña"
                placeholder="Confirmar contraseña"
                maxlength="20"
                type="password"
                v-model="passwordConfirm"
              />
            </div>
          </div>
          <div class="mb-3">
            <small :style="{ color: passwordStatus.longitud ? 'green':'red' }">Al menos 8 caracteres.</small><br>
            <small :style="{ color: passwordStatus.minuscula ? 'green':'red' }">Al menos una letra minúscula.</small><br>
            <small :style="{ color: passwordStatus.mayuscula ? 'green':'red' }">Al menos una letra mayúscula.</small><br>
            <small :style="{ color: passwordStatus.numero ? 'green':'red' }">Al menos un número.</small><br>
            <small :style="{ color: passwordStatus.especial ? 'green':'red' }">Al menos un carácter especial (!@#$%^&*.).</small><br>
            <small :style="{ color: passwordStatus.iguales ? 'green':'red' }">Contraseña y confirmación iguales.</small>
          </div>

          <div class="text-right mt-4">
            <b-button
              variant="primary"
              :disabled="!allQuestionsAnswered || !isPasswordValid || loading"
              @click="submitAnswers"
            >
              Verificar y generar certificado
            </b-button>
          </div>
        </div>
      </template>

      <!-- Banco: en proceso -->
      <template v-if="step === 'BANK_PENDING'">
        <div class="text-center">
          <div class="mb-4">
            <feather-icon icon="ClockIcon" size="48" class="text-primary" />
          </div>
          <h5>Verificación en proceso</h5>
          <p style="color: #4e4e4e; font-size: 0.95rem;">
            Estamos verificando su transferencia bancaria. Este proceso puede demorar unos segundos.
          </p>
          <p style="color: #4e4e4e; font-size: 0.95rem;">
            <strong>No cierre esta página.</strong> Será redirigido automáticamente una vez confirmada su identidad.
          </p>
          <div class="mt-3">
            <b-spinner variant="primary" label="Verificando..." />
          </div>
        </div>
      </template>

      <!-- Generando certificado -->
      <template v-if="step === 'CERTIFICATE_QUEUED'">
        <div class="text-center">
          <div class="mb-4">
            <feather-icon icon="ShieldIcon" size="48" class="text-warning" />
          </div>
          <h5>Generando certificado de firma</h5>
          <p style="color: #4e4e4e; font-size: 0.95rem;">
            Estamos generando su certificado de firma electrónica avanzada.
          </p>
          <p style="color: #4e4e4e; font-size: 0.95rem;">
            <strong>No cierre esta página.</strong> Será redirigido automáticamente en unos segundos.
          </p>
          <div class="mt-3">
            <b-spinner variant="warning" label="Generando certificado..." />
          </div>
        </div>
      </template>

      <!-- Error -->
      <template v-if="step === 'ERROR'">
        <div class="text-center">
          <div class="mb-4">
            <feather-icon icon="XCircleIcon" size="48" class="text-danger" />
          </div>
          <h5 class="text-danger">Verificación fallida</h5>
          <p style="color: #4e4e4e; font-size: 0.95rem;">
            No fue posible verificar su identidad. Esto puede ocurrir si los datos ingresados no coinciden o si hubo un problema con el proceso de verificación.
          </p>
          <div class="bg-light rounded p-3 mb-3 text-left" style="border-left: 3px solid #ea5455;">
            <p class="mb-1" style="color: #4e4e4e; font-size: 0.85rem;">
              <strong>Importante:</strong> El sistema permite un máximo de 3 intentos de verificación cada 24 horas.
              Si ha agotado sus intentos, deberá esperar antes de volver a intentarlo.
            </p>
          </div>
          <div class="mt-4">
            <b-button variant="primary" @click="retry">
              <feather-icon icon="RefreshCwIcon" class="mr-1" />
              Intentar nuevamente
            </b-button>
          </div>
        </div>
      </template>
    </b-card>
  </b-overlay>
</template>

<script>
import { mapGetters } from 'vuex';
import { signConfigStamp } from '@/febos/servicios/api/usuarios.api';

export default {
  data() {
    return {
      loading: false,
      step: 'SELECT_METHOD',
      nfcEnabled: true,
      bankTransferEnabled: true,
      sToken: null,
      securityQuestions: [],
      securityAnswers: {},
      password: '',
      passwordConfirm: '',
      countdown: 600,
      countdownInterval: null,
      countdownExpired: false,
      pollingInterval: null,
    };
  },
  computed: {
    ...mapGetters('Empresas', ['company']),
    ...mapGetters('Usuario', ['currentUser']),
    countdownFormatted() {
      const min = Math.floor(this.countdown / 60);
      const sec = this.countdown % 60;
      return `${min}:${sec.toString().padStart(2, '0')}`;
    },
    allQuestionsAnswered() {
      if (!this.securityQuestions || this.securityQuestions.length === 0) return false;
      return this.securityQuestions.every(q => this.securityAnswers[q.questionId]);
    },
    passwordStatus() {
      return {
        longitud: this.password && this.password.length >= 8,
        minuscula: this.password && /[a-z]/.test(this.password),
        mayuscula: this.password && /[A-Z]/.test(this.password),
        numero: this.password && /\d/.test(this.password),
        especial: this.password && /[!@#$%^&*().]/.test(this.password),
        iguales: this.password && this.passwordConfirm === this.password
      };
    },
    isPasswordValid() {
      return Object.values(this.passwordStatus).every(v => v);
    }
  },
  methods: {
    async loadVerificationFlags() {
      try {
        const response = await signConfigStamp({
          categoria: 'GET_VERIFICATION_FLAGS',
          idUsuarioFirma: this.currentUser.id,
          parametros: {},
          proveedor: this.company.firmaAvanzada,
          tipoFirma: 'firmaAvanzada'
        });
        const params = response.data && response.data.parametros ? response.data.parametros : {};
        if (params.nfcEnabled !== undefined) this.nfcEnabled = params.nfcEnabled;
        if (params.bankTransferEnabled !== undefined) this.bankTransferEnabled = params.bankTransferEnabled;
      } catch (e) {
        // Usar defaults
      }
    },
    formatOptions(answerChoice) {
      if (!answerChoice) return [];
      return answerChoice.map(opt => ({
        text: opt.value || opt.text,
        value: opt.answerId || opt.id
      }));
    },
    async startVerification(method) {
      this.loading = true;
      try {
        if (method === 'NFC') {
          const response = await signConfigStamp({
            categoria: 'REQUEST_NFC',
            idUsuarioFirma: this.currentUser.id,
            parametros: { sToken: this.sToken },
            proveedor: this.company.firmaAvanzada,
            tipoFirma: 'firmaAvanzada'
          });

          if (response.data && response.data.parametros && response.data.parametros.alreadyValidated === 'true') {
            this.loading = false;
            this.step = 'LOADING_QUESTIONS';
            await this.requestQuestions();
            return;
          }

          this.loading = false;
          this.step = 'NFC_PENDING';
          this.startCountdown();
          this.startPolling();
        } else if (method === 'BANK_TRANSFER') {
          const basePath = `${window.location.origin}${window.location.pathname}`;
          const returnUrl = `${basePath}?status=ok&token=${this.sToken}&pendingVerification=bank`;
          const cancelUrl = `${basePath}?status=error&token=${this.sToken}`;

          const response = await signConfigStamp({
            categoria: 'REQUEST_2FA_BANK',
            idUsuarioFirma: this.currentUser.id,
            parametros: {
              sToken: this.sToken,
              returnUrl,
              cancelUrl
            },
            proveedor: this.company.firmaAvanzada,
            tipoFirma: 'firmaAvanzada'
          });

          this.loading = false;
          const bankUrl = response.data && response.data.parametros ? response.data.parametros.bankTransferUrl : '';
          if (bankUrl) {
            window.location.href = bankUrl;
          } else {
            this.step = 'ERROR';
          }
        }
      } catch (error) {
        this.loading = false;
        this.step = 'ERROR';
        this.$bvToast.toast(
          error.response?.data?.errores?.[0] || 'Error al iniciar verificación',
          { title: 'Error', variant: 'danger', solid: true, noAutoHide: true }
        );
      }
    },
    startCountdown() {
      this.countdown = 600;
      this.countdownExpired = false;
      if (this.countdownInterval) clearInterval(this.countdownInterval);
      this.countdownInterval = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) {
          clearInterval(this.countdownInterval);
          this.countdownExpired = true;
        }
      }, 1000);
    },
    startPolling() {
      if (this.pollingInterval) clearInterval(this.pollingInterval);
      this.pollingInterval = setInterval(() => this.pollStatus(), 5000);
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    async pollStatus() {
      try {
        const response = await signConfigStamp({
          categoria: 'STATUS',
          idUsuarioFirma: this.currentUser.id,
          parametros: { sToken: this.sToken },
          proveedor: this.company.firmaAvanzada,
          tipoFirma: 'firmaAvanzada'
        });
        const status = response.data && response.data.parametros ? response.data.parametros.status : '';

        if (status === 'NFC_SUCCESS') {
          this.stopPolling();
          if (this.countdownInterval) clearInterval(this.countdownInterval);
          this.step = 'LOADING_QUESTIONS';
          await this.requestQuestions();
        } else if (status === 'CERTIFICATE_QUEUED') {
          this.step = 'CERTIFICATE_QUEUED';
        } else if (status === 'CERTIFICATE_CREATED') {
          this.stopPolling();
          this.verificationComplete();
        } else if (status === 'ERROR' || (status && status !== 'INITIATED')) {
          this.stopPolling();
          this.step = 'ERROR';
        }
      } catch (error) {
        // Silenciar errores de polling
      }
    },
    async requestQuestions() {
      try {
        const response = await signConfigStamp({
          categoria: 'REQUEST_QUESTIONS',
          idUsuarioFirma: this.currentUser.id,
          parametros: { sToken: this.sToken },
          proveedor: this.company.firmaAvanzada,
          tipoFirma: 'firmaAvanzada'
        });
        const parametros = response.data && response.data.parametros ? response.data.parametros : {};
        let questions = parametros.question || [];

        // Normalizar preguntas (pueden venir como myArrayList)
        if (questions.myArrayList) {
          questions = questions.myArrayList.map(q => {
            const item = q.map || q;
            return {
              questionId: item.questionId,
              questionText: item.questionText,
              answerChoice: item.answerChoice && item.answerChoice.myArrayList
                ? item.answerChoice.myArrayList.map(a => ({ answerId: (a.map || a).answerId, value: (a.map || a).value }))
                : (item.answerChoice || [])
            };
          });
        }

        this.securityQuestions = questions;
        this.securityAnswers = {};
        this.step = 'QUESTIONS';
      } catch (error) {
        this.step = 'ERROR';
        this.$bvToast.toast(
          error.response?.data?.errores?.[0] || 'Error al solicitar preguntas',
          { title: 'Error', variant: 'danger', solid: true, noAutoHide: true }
        );
      }
    },
    async submitAnswers() {
      this.loading = true;
      try {
        const answers = this.securityQuestions.map(q => ({
          questionId: q.questionId,
          answerId: this.securityAnswers[q.questionId]
        }));

        await signConfigStamp({
          categoria: 'VL2FA',
          idUsuarioFirma: this.currentUser.id,
          parametros: {
            sToken: this.sToken,
            password: this.password,
            answers
          },
          proveedor: this.company.firmaAvanzada,
          tipoFirma: 'firmaAvanzada'
        });

        this.loading = false;
        this.verificationComplete();
      } catch (error) {
        this.loading = false;
        this.$bvToast.toast(
          error.response?.data?.errores?.[0] || 'Error al verificar respuestas',
          { title: 'Error', variant: 'danger', solid: true, noAutoHide: true }
        );
      }
    },
    verificationComplete() {
      this.$bvToast.toast(
        'Certificado generado exitosamente. Su firma electrónica avanzada está lista.',
        { title: 'Verificación exitosa', variant: 'success', solid: true, autoHideDelay: 3000 }
      );
      setTimeout(() => {
        const basePath = window.location.origin;
        const ambit = window.location.pathname.match(/cloud|proveedores|cliente/i);
        window.location.href = `${basePath}/${ambit ? ambit[0] : 'cloud'}/usuario/perfil`;
      }, 2000);
    },
    retry() {
      this.step = 'SELECT_METHOD';
      this.securityQuestions = [];
      this.securityAnswers = {};
      this.password = '';
      this.passwordConfirm = '';
      this.countdownExpired = false;
    }
  },
  created() {
    this.sToken = this.$route.query.token;

    if (!this.sToken) {
      this.step = 'ERROR';
      return;
    }

    // Cargar feature flags
    this.loadVerificationFlags();

    // Si viene de callback de banco
    if (this.$route.query.pendingVerification === 'bank') {
      this.step = 'BANK_PENDING';
      this.startPolling();
      return;
    }

    // Si viene con error
    if (this.$route.query.status === 'error') {
      this.step = 'ERROR';
      return;
    }
  },
  beforeDestroy() {
    if (this.countdownInterval) clearInterval(this.countdownInterval);
    if (this.pollingInterval) clearInterval(this.pollingInterval);
  }
};
</script>
